import React, { useMemo } from 'react';
import i18n from '@/i18n';
import { Eye } from 'lucide-react';
import WorkbenchPreviewImage from '@/components/ipe/WorkbenchPreviewImage';
import { useSignedImageSrc } from '@/hooks/useSignedImageSrc';
import { IPE_SOURCING_MATERIAL_IMAGE_ROOT_MARGIN } from '@/constants/lazyLoadConfig';
import { isManagedStorageUrl } from '@/utils/imageDisplayAccess';
import { resolveImageSpaceDisplayRawUrl } from '@/pages/ipe/workbench/utils/imageSpaceDisplayUrl';
import { resolveGoodsPrintUrl } from '@/pages/ipe/workbench/utils/goodsPrintUrlResolve';
import {
  pickVideoPosterSignOptions,
  pickVideoSpaceSignOptions,
  resolveSafeVideoPlaySrc,
  resolveVideoSpacePosterUrl,
} from '@/pages/ipe/workbench/utils/videoSpaceDisplayAccess';
import { resolveVideoSpacePlayUrl } from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';
import { stripImageFileExtension } from '@/utils/imageUrlUtils';

function LibraryModalVideoThumb({ image, scrollRoot, viewRootMargin, onError, title }) {
  const poster = useMemo(() => resolveVideoSpacePosterUrl(image), [image]);
  const posterSignOptions = useMemo(
    () => pickVideoPosterSignOptions(image, poster),
    [image, poster],
  );
  const playRaw = useMemo(() => resolveVideoSpacePlayUrl(image), [image]);
  const playSignOptions = useMemo(
    () => pickVideoSpaceSignOptions(image, playRaw),
    [image, playRaw],
  );
  const signedPlayUrl = useSignedImageSrc(playRaw, playSignOptions);
  const videoThumbSrc = resolveSafeVideoPlaySrc(signedPlayUrl, playRaw);

  if (poster) {
    if (isManagedStorageUrl(poster)) {
      return (
        <WorkbenchPreviewImage
          url={poster}
          displayUrl={posterSignOptions.displayUrl}
          viewUrl={posterSignOptions.viewUrl}
          record={image}
          alt={title}
          viewRoot={scrollRoot}
          viewRootMargin={viewRootMargin}
          onError={onError}
        />
      );
    }
    return (
      <img
        className="funding-library-modal__media-video-thumb"
        src={poster}
        alt={title}
        loading="lazy"
        decoding="async"
        onError={onError}
      />
    );
  }

  if (videoThumbSrc) {
    return (
      <video
        className="funding-library-modal__media-video-thumb"
        src={videoThumbSrc}
        muted
        playsInline
        preload="metadata"
        aria-hidden
      />
    );
  }

  return (
    <span className="funding-library-modal__media-empty">{i18n.t('workbench:spaceBoard.noCover')}</span>
  );
}

export default function LibraryModalImageCard({
  image,
  /** @deprecated 优先用 urlMap + image 记录解析，与图片空间一致 */
  imageUrl = '',
  urlMap = {},
  printUrlOverrides = null,
  scrollRoot = null,
  viewRootMargin = IPE_SOURCING_MATERIAL_IMAGE_ROOT_MARGIN,
  selected = false,
  /** 预览已判定失败：停止挂载 SignedImage，避免 onError 重试风暴 */
  loadFailed = false,
  onToggle,
  onPreview,
  onImageError,
  loadingLabel = '图片加载中...',
  failedLabel = '加载失败',
}) {
  const title = stripImageFileExtension(image?.name) || image?.name || image?.id || '图片';
  const isVideo = image?._mediaKind === 'video';

  const rawUrl = useMemo(() => {
    if (isVideo) return '';
    const override = printUrlOverrides
      ? resolveGoodsPrintUrl(image?.id, printUrlOverrides)
      : '';
    if (override) return override;
    if (imageUrl) return imageUrl;
    return resolveImageSpaceDisplayRawUrl(image, urlMap);
  }, [image, imageUrl, urlMap, printUrlOverrides, isVideo]);

  const canAttemptPreview = isVideo || (!loadFailed && (
    Boolean(rawUrl)
    || Boolean(image?.url || image?.src || image?.dataUrl || image?.viewUrl || image?.displayUrl)
  ));

  const handleToggle = (event) => {
    event?.stopPropagation?.();
    onToggle?.(image.id);
  };

  const handlePreview = (event) => {
    event?.stopPropagation?.();
    onPreview?.(image);
  };

  return (
    <article
      className={`funding-library-modal__media-card${selected ? ' funding-library-modal__media-card--selected' : ''}`}
    >
      <div className="funding-library-modal__media-thumb">
        {canAttemptPreview ? (
          <>
            <button
              type="button"
              className="funding-library-modal__media-preview"
              onClick={handleToggle}
              aria-label={i18n.t('workbench:spaceBoard.selectNamed', { name: title })}
            >
              {isVideo ? (
                loadFailed ? (
                  <span className="funding-library-modal__media-empty funding-library-modal__media-empty--failed">
                    {failedLabel}
                  </span>
                ) : (
                  <LibraryModalVideoThumb
                    image={image}
                    scrollRoot={scrollRoot}
                    viewRootMargin={viewRootMargin}
                    onError={onImageError}
                    title={title}
                  />
                )
              ) : rawUrl ? (
                <WorkbenchPreviewImage
                  url={rawUrl}
                  record={image}
                  alt={title}
                  viewRoot={scrollRoot}
                  viewRootMargin={viewRootMargin}
                  onError={onImageError}
                />
              ) : (
                <span className="funding-library-modal__media-empty">{loadingLabel}</span>
              )}
            </button>
            <div className="funding-library-modal__media-overlay" aria-hidden />
            {onPreview ? (
              <button
                type="button"
                className="funding-library-modal__media-eye"
                aria-label={i18n.t('workbench:materialHub.previewNamed', { title })}
                onClick={handlePreview}
              >
                <Eye size={22} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </>
        ) : (
          <div className={`funding-library-modal__media-empty${loadFailed ? ' funding-library-modal__media-empty--failed' : ''}`}>
            {loadFailed ? failedLabel : loadingLabel}
          </div>
        )}
      </div>
      <label className="funding-library-modal__media-body funding-library-modal__media-meta">
        <input
          type="checkbox"
          checked={selected}
          onChange={handleToggle}
          aria-label={i18n.t('workbench:spaceBoard.selectNamed', { name: title })}
        />
        <span className="funding-library-modal__media-name" title={title}>
          {title}
        </span>
      </label>
    </article>
  );
}
